import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Search, Play, MessageCircle, Sparkles, Eye, Clock, Tag as TagIcon,
  PlayCircle, GraduationCap, X,
} from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" }) : "";

export default function Tutoriais() {
  const [data, setData] = useState({ settings: {}, tutorials: [], categories: [] });
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/tutorials", { params: { q: search || undefined, category: category === "all" ? undefined : category } });
      setData(r.data);
    } catch { toast.error("Erro ao carregar tutoriais"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  // Debounce: re-roda 250ms após o usuário parar de digitar/trocar de categoria
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search, category]);

  const featured = useMemo(() => data.tutorials.filter(t => t.featured), [data.tutorials]);
  const mostViewed = useMemo(() => [...data.tutorials].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 6), [data.tutorials]);

  const openTutorial = async (t) => {
    try { api.post(`/tutorials/${t.id}/view`).catch(() => {}); } catch {}
    window.open(t.youtube_url, "_blank", "noopener,noreferrer");
  };

  const supportClick = () => {
    const num = String(data.settings.whatsapp_support || "").replace(/\D/g, "");
    if (!num) {
      toast.info("WhatsApp de suporte não configurado pelo administrador.");
      return;
    }
    const full = num.startsWith("55") ? num : "55" + num;
    const msg = encodeURIComponent(data.settings.whatsapp_message || "Olá! Preciso de ajuda com o sistema.");
    window.open(`https://wa.me/${full}?text=${msg}`, "_blank", "noopener,noreferrer");
  };

  if (!data.settings.enabled && data.settings.enabled !== undefined && !loading) {
    return (
      <div className="text-center py-24 text-neutral-500">
        <GraduationCap size={48} className="mx-auto mb-4 opacity-30"/>
        <h2 className="font-display text-2xl">Central de ajuda em manutenção</h2>
        <p className="text-sm mt-2">Volte em breve.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-32" data-testid="tutoriais-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="label-tiny mb-2">Aprenda no seu ritmo</p>
          <h1 className="font-display text-4xl tracking-tight">{data.settings.menu_label || "Central de Ajuda"}</h1>
          <p className="text-sm text-neutral-500 mt-1">Vídeos curtos e diretos pra dominar cada parte do sistema.</p>
        </div>
      </div>

      {/* Busca */}
      <Card className="p-4 border-[#E5E5E5] shadow-none">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"/>
          <Input
            value={search}
            onChange={e=>setSearch(e.target.value)}
            placeholder="Buscar: como fazer venda, cadastrar produto, ver lucro…"
            className="pl-9 h-11"
            data-testid="tutoriais-search"
          />
          {search && (
            <button onClick={()=>setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700">
              <X size={14}/>
            </button>
          )}
        </div>
      </Card>

      {/* Filtro de categorias */}
      {(data.categories || []).length > 0 && (
        <div className="flex gap-2 flex-wrap" data-testid="tutoriais-categories">
          <button
            onClick={()=>setCategory("all")}
            className={`text-xs px-3 py-2 rounded-full border transition ${
              category === "all"
                ? "bg-[#0A0A0A] text-white border-[#0A0A0A] dark:bg-white dark:text-[#0A0A0A]"
                : "bg-white border-[#E5E5E5] hover:border-neutral-400 dark:bg-transparent dark:border-white/10 dark:text-neutral-300"
            }`}
          >
            Todos
          </button>
          {data.categories.map(c => (
            <button
              key={c}
              onClick={()=>setCategory(c)}
              className={`text-xs px-3 py-2 rounded-full border transition ${
                category === c
                  ? "bg-[#0A0A0A] text-white border-[#0A0A0A] dark:bg-white dark:text-[#0A0A0A]"
                  : "bg-white border-[#E5E5E5] hover:border-neutral-400 dark:bg-transparent dark:border-white/10 dark:text-neutral-300"
              }`}
              data-testid={`tutoriais-cat-${c}`}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      {loading && <div className="text-center py-12 text-neutral-500 text-sm">Carregando tutoriais…</div>}

      {/* Em destaque */}
      {!loading && featured.length > 0 && category === "all" && !search && (
        <section data-testid="tutoriais-featured">
          <h2 className="font-display text-xl mb-4 flex items-center gap-2"><Sparkles size={18} className="text-amber-500"/> Em destaque</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {featured.map(t => <TutorialCard key={t.id} t={t} onOpen={openTutorial} large/>)}
          </div>
        </section>
      )}

      {/* Mais assistidos */}
      {!loading && mostViewed.some(t => (t.views || 0) > 0) && category === "all" && !search && (
        <section>
          <h2 className="font-display text-xl mb-4 flex items-center gap-2"><Eye size={18} className="text-emerald-500"/> Mais assistidos</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {mostViewed.filter(t => (t.views || 0) > 0).map(t => <TutorialCard key={t.id} t={t} onOpen={openTutorial}/>)}
          </div>
        </section>
      )}

      {/* Lista principal */}
      {!loading && (
        <section>
          <h2 className="font-display text-xl mb-4">
            {search ? `Resultados para "${search}"` : category !== "all" ? category : "Todos os tutoriais"}
          </h2>
          {data.tutorials.length === 0 ? (
            <div className="text-center py-16 text-neutral-500">
              <PlayCircle size={48} className="mx-auto mb-3 opacity-30"/>
              <p className="text-sm">Nenhum tutorial encontrado.</p>
              {(search || category !== "all") && <button onClick={()=>{ setSearch(""); setCategory("all"); }} className="text-xs text-emerald-600 mt-2 hover:underline">Limpar filtros</button>}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {data.tutorials.map(t => <TutorialCard key={t.id} t={t} onOpen={openTutorial}/>)}
            </div>
          )}
        </section>
      )}

      {/* CTA suporte premium flutuante */}
      <button
        onClick={supportClick}
        className="fixed bottom-5 right-5 sm:bottom-8 sm:right-8 z-40 group bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white rounded-full shadow-2xl shadow-emerald-900/30 px-4 sm:px-5 py-3 flex items-center gap-2 transition-all hover:scale-105 hover:-translate-y-0.5"
        data-testid="tutoriais-support-button"
        title="Falar com o suporte"
      >
        <span className="absolute inset-0 rounded-full bg-emerald-400 opacity-30 group-hover:opacity-50 blur-lg -z-10 animate-pulse"/>
        <MessageCircle size={18}/>
        <span className="hidden sm:inline font-medium text-sm">Pedir ajuda ao suporte</span>
      </button>
    </div>
  );
}

function TutorialCard({ t, onOpen, large = false }) {
  return (
    <button
      onClick={()=>onOpen(t)}
      className="group text-left rounded-xl overflow-hidden border border-[#E5E5E5] dark:border-white/10 bg-white dark:bg-white/[0.02] hover:border-emerald-400 hover:shadow-md hover:-translate-y-0.5 transition-all"
      data-testid={`tutorial-${t.id}`}
    >
      <div className="relative aspect-video bg-black overflow-hidden">
        {t.thumbnail && (
          <img src={t.thumbnail} alt={t.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"/>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white/90 dark:bg-black/80 grid place-items-center shadow-xl group-hover:scale-110 transition">
            <Play size={20} className="text-emerald-600 dark:text-emerald-400 ml-1" fill="currentColor"/>
          </div>
        </div>
        {(t.views || 0) > 0 && (
          <div className="absolute bottom-2 right-2 inline-flex items-center gap-1 text-[10px] text-white bg-black/60 backdrop-blur-sm rounded px-1.5 py-0.5">
            <Eye size={10}/> {t.views}
          </div>
        )}
        {t.featured && (
          <div className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] text-white bg-amber-500 rounded px-1.5 py-0.5 font-medium">
            <Sparkles size={10}/> Destaque
          </div>
        )}
      </div>
      <div className="p-3 sm:p-4">
        {t.category && <p className="text-[10px] uppercase tracking-widest text-emerald-600 dark:text-emerald-400 font-medium mb-1">{t.category}</p>}
        <h3 className={`font-display font-medium leading-tight ${large ? "text-base sm:text-lg" : "text-sm"}`}>{t.title}</h3>
        {t.description && <p className="mt-2 text-[11px] sm:text-xs text-neutral-500 line-clamp-2 leading-relaxed">{t.description}</p>}
        <div className="mt-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 flex-wrap min-w-0">
            {(t.tags || []).slice(0, 2).map(tag => (
              <span key={tag} className="text-[9px] px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-white/5 text-neutral-600 dark:text-neutral-400 truncate max-w-[80px]">
                {tag}
              </span>
            ))}
          </div>
          <span className="text-[10px] text-neutral-400 inline-flex items-center gap-1 shrink-0"><Clock size={10}/> {fmtDate(t.created_at)}</span>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
            Assistir Tutorial <span className="group-hover:translate-x-0.5 transition-transform">→</span>
          </span>
        </div>
      </div>
    </button>
  );
}
